import type { DiaProjetadoOut } from "../../lib/projecao/tipos";

/*
 * Indicadores das flags por dia (FE-2 CP-D).
 *
 * `DiaProjetadoOut` carrega 4 booleanos que sinalizam características do dia
 * projetado. Esta lista renderiza um indicador SÓ para cada flag `true` (dia
 * sem flag alguma → nada renderizado). Vale para AS DUAS visões (empresa e
 * grupo), pois ambas usam a mesma `DiaProjetadoOut`.
 *
 * Cada indicador é sóbrio (ponto colorido com tokens CPMH, `text-flame` para os
 * de atenção) e ACESSÍVEL: o rótulo curto é decorativo (`aria-hidden`) e o
 * significado completo vai em `title` + `aria-label`/texto visível-a-leitor.
 */

/**
 * Catálogo das flags na ordem de exibição. Cada entrada amarra:
 * - `campo`: a propriedade booleana de `DiaProjetadoOut`;
 * - `rotuloCurto`: marca visual compacta;
 * - `rotuloCompleto`: descrição PT-BR para o usuário financeiro (vai em
 *   `title`/aria — é o que o leitor de tela e o tooltip expõem).
 */
const FLAGS: ReadonlyArray<{
  campo: keyof Pick<
    DiaProjetadoOut,
    | "vencida_puxada"
    | "receita_atrasada_nao_conciliada"
    | "conciliacao_pendente"
    | "alem_horizonte"
  >;
  rotuloCurto: string;
  rotuloCompleto: string;
}> = [
  {
    campo: "vencida_puxada",
    rotuloCurto: "Vencida",
    rotuloCompleto: "Título vencido puxado para o dia",
  },
  {
    campo: "receita_atrasada_nao_conciliada",
    rotuloCurto: "Receita atrasada",
    rotuloCompleto: "Receita atrasada ainda não conciliada",
  },
  {
    campo: "conciliacao_pendente",
    rotuloCurto: "Conciliação pendente",
    rotuloCompleto: "Há conciliação pendente afetando o dia",
  },
  {
    campo: "alem_horizonte",
    rotuloCurto: "Além do horizonte",
    rotuloCompleto: "Dia além do horizonte de lançamentos do ERP",
  },
];

interface IndicadoresDiaProps {
  dia: DiaProjetadoOut;
}

export function IndicadoresDia({ dia }: IndicadoresDiaProps): JSX.Element | null {
  const ativos = FLAGS.filter((flag) => dia[flag.campo]);

  if (ativos.length === 0) {
    return null;
  }

  return (
    <ul className="flex flex-wrap gap-2">
      {ativos.map((flag) => (
        <li
          key={flag.campo}
          title={flag.rotuloCompleto}
          aria-label={flag.rotuloCompleto}
          className="inline-flex items-center gap-1 rounded-sm border border-platinum px-2 py-1 text-xs text-flame"
        >
          <span aria-hidden="true">{flag.rotuloCurto}</span>
        </li>
      ))}
    </ul>
  );
}
